import React, { useState, useEffect } from "react";
import {
  Typography,
  ThemeProvider,
  CssBaseline,
  Box,
  Snackbar,
  Alert,
} from "@mui/material";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useNavigate,
} from "react-router-dom";
import { theme } from "./App.styles";
import Home from "./Home";
import AuthPage from "./components/auth/AuthPage";
import { auth } from "./config/firebase";
import { onAuthStateChanged } from "firebase/auth";
import { isTokenExpired, logout } from "./services/API";
import AuthActionHandler from "./components/auth/AuthActionHandler";
import Index from "./components/landingPage/Index";

const AppContent = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showExpirationAlert, setShowExpirationAlert] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    // Listen for global errors
    const handleGlobalError = (event) => {
      setErrorMessage(event.detail?.message || "An unexpected error occurred");
    };

    window.addEventListener("globalError", handleGlobalError);

    return () => {
      window.removeEventListener("globalError", handleGlobalError);
    };
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      // Skip auth check for password reset route
      if (
        window.location.pathname === "/reset_password" ||
        window.location.pathname === "/auth/action"
      ) {
        setLoading(false);
        return;
      }

      if (user) {
        // Check if email is verified
        if (!user.emailVerified) {
          // If email is not verified, sign out and redirect to auth
          await auth.signOut();
          localStorage.removeItem("token");
          localStorage.setItem("needsVerification", "true");
          localStorage.setItem("pendingVerificationEmail", user.email);
          navigate("/auth");
          setUser(null);
          setLoading(false);
          return;
        }

        // Get the ID token and store it
        const idToken = await user.getIdToken();
        localStorage.setItem("token", idToken);
        localStorage.removeItem("needsVerification");
        localStorage.removeItem("pendingVerificationEmail");
      } else {
        // Remove token if user is not authenticated
        localStorage.removeItem("token");
        // Only redirect to auth if not on password reset page
        if (
          window.location.pathname !== "/reset_password" &&
          window.location.pathname !== "/auth/action" &&
          window.location.pathname !== "/"
        ) {
          navigate("/auth");
        }
      }
      setUser(user);
      setLoading(false);
    });

    // Check token expiration every minute
    const tokenCheckInterval = setInterval(() => {
      if (user && isTokenExpired()) {
        setShowExpirationAlert(true);
        setTimeout(() => {
          logout();
          navigate("/auth"); // Redirect to auth page after logout
        }, 3000); // Give user 3 seconds to see the alert before logout
      }
    }, 60000); // Check every minute

    // Cleanup subscription and interval on unmount
    return () => {
      unsubscribe();
      clearInterval(tokenCheckInterval);
    };
  }, [navigate]); // Add navigate to dependencies

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          backgroundColor: theme.palette.background.default,
        }}
      >
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  // If showing landing page, don't wrap with Material-UI
  if (!user && window.location.pathname === "/") {
    return (
      <div>
        <Routes>
          <Route path="/" element={<Index />} />
          <Route path="/auth" element={<AuthPage />} />
          <Route path="/auth/action" element={<AuthActionHandler />} />
        </Routes>
      </div>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Snackbar
        open={showExpirationAlert}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity="warning" onClose={() => setShowExpirationAlert(false)}>
          Your session has expired. You will be logged out shortly.
        </Alert>
      </Snackbar>
      <Snackbar
        open={!!errorMessage}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        autoHideDuration={6000}
        onClose={() => setErrorMessage(null)}
      >
        <Alert severity="error" onClose={() => setErrorMessage(null)}>
          {errorMessage}
        </Alert>
      </Snackbar>
      <Box
        sx={{
          minHeight: "100vh",
          backgroundColor: theme.palette.background.default,
          position: "relative",
        }}
      >
        <Box component="main" sx={{ minHeight: "100vh" }}>
          <Routes>
            <Route path="/auth/action" element={<AuthActionHandler />} />
            <Route
              path="/auth"
              element={user ? <Navigate to="/" replace /> : <AuthPage />}
            />
            <Route path="/" element={user ? <Home /> : <Index />} />
          </Routes>
        </Box>
      </Box>
    </ThemeProvider>
  );
};

// Export the global error event for use in other files
export const showGlobalError = (message) => {
  window.dispatchEvent(new CustomEvent("globalError", { detail: { message } }));
};

const App = () => {
  return (
    <Router
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true,
      }}
    >
      <AppContent />
    </Router>
  );
};

export default App;
